import './DisconnectPageModal.scss';
import React, { KeyboardEvent } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, withHandlers, lifecycle, withState } from 'recompose';
import { ModalBody, ModalHeader, ModalFooter } from '../modal-helpers';
import Button from '../../buttons/DeprecatedButton/DeprecatedButton';
import { ENTER_KEY_NAME } from '@mm/constants/keys-codes';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import {
  modalDescription,
  modalTitle,
  igModalTitle,
  igModalDescription,
} from './constants';
import { Dispatch } from 'redux';
import { StoreState } from '@mm/state/store-interfaces';
import { LifeCycleThis } from '@mm/utils';
import { RouterComponentProps } from '../../helpers/routes/interfaces';
import { ModalInstanceProps } from '../interfaces';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { CHATBOT_EDITOR } from '../../constants/routes';

type OuterProps = ModalInstanceProps & {
  page: ConnectedPageModel;
  instagramMode?: boolean;
  onDisconnect?: () => void;
};

type Handlers = {
  onDisconnectClick: () => void;
  onKeyDown: (e: Event) => void;
};

type StateAndHandlers = {
  isDisconnecting: boolean;
  setIsDisconnecting: (isDisconnecting: boolean) => void;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  Handlers &
  StateAndHandlers &
  RouterComponentProps;

type Props = OuterProps & InnerProps;

const DisconnectPageModal = (props: Props) => {
  const {
    onDisconnectClick,
    closeModal,
    page,
    isDisconnecting,
    instagramMode,
    onDisconnect = () => {},
  } = props;

  const pageName = page?.name ?? '';
  const title = instagramMode ? igModalTitle : modalTitle;
  const description = instagramMode
    ? igModalDescription(pageName)
    : modalDescription;
  const customDisconnect = () => {
    onDisconnect();
    closeModal();
  };

  return (
    <div className="disconnect-page-modal">
      <ModalHeader className="disconnect-page-modal__header">
        {title}
      </ModalHeader>
      <ModalBody className="disconnect-page-modal__body">
        {description}
        {!instagramMode && (
          <span className="disconnect-page-modal__name">{pageName}.</span>
        )}
      </ModalBody>
      <ModalFooter>
        <Button invert onClick={closeModal}>
          Cancel
        </Button>

        <Button
          disabled={isDisconnecting}
          red
          onClick={instagramMode ? customDisconnect : onDisconnectClick}
        >
          Disconnect
        </Button>
      </ModalFooter>
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  deleteConnection: (
    connectedPage: ConnectedPageModel,
    companyId: number,
    paginationPage: number,
  ) =>
    dispatch(
      connectedPageCRUDActions.delete(connectedPage, companyId, paginationPage),
    ),
});

const mapStateToProps = (state: StoreState) => ({
  paginationPage: state.view.activeDashboardPaginationPage,
  company: activeCompanySelector(state),
});

const onDisconnectClick = (props: Props) => async () => {
  const {
    deleteConnection,
    page,
    closeModal,
    company,
    paginationPage,
    setIsDisconnecting,
    isDisconnecting,
    history,
  } = props;

  if (isDisconnecting || !company) {
    return;
  }

  setIsDisconnecting(true);

  await deleteConnection(page, company.id, paginationPage);
  history.push(CHATBOT_EDITOR);
  closeModal();
};

const onKeyDown =
  ({ onDisconnectClick }: Props) =>
  (e: KeyboardEvent<Document>) => {
    if (e.key === ENTER_KEY_NAME) {
      onDisconnectClick();
    }
  };

function componentWillMount(this: LifeCycleThis<Props>) {
  document.addEventListener('keydown', this.props.onKeyDown);
}

function componentWillUnmount(this: LifeCycleThis<Props>) {
  document.removeEventListener('keydown', this.props.onKeyDown);
}

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withState('isDisconnecting', 'setIsDisconnecting', false),
  withHandlers({ onDisconnectClick }),
  withHandlers({ onKeyDown }),
  lifecycle<Props, {}>({ componentWillMount, componentWillUnmount }),
);

export default enhance(DisconnectPageModal);
